import { useCallback } from 'react';
import { getSearchAppName } from 'src/utils/app';
import { INDEX_FIELDS } from 'src/utils/entityDefinitions';
import AggregationBuilder from 'src/components/BeaconV2Components/utils/AggregationBuilder';
import { useAppSelector } from 'src/utils/Hooks';

export interface Field {
  name: string;
  canBeExported?: boolean;
  /**
   * Column title for the field name in a table.
   */
  displayName?: string;
  /**
   * Optional formatter for the comparison value in
   * the table. If not provided, will default to percent
   * change.
   */
  comparisonFormatter?: (value: number, difference: number) => React.ReactNode;
}

export interface UseAggregationBuilderWithMetricsArgs {
  indexName: string;
  fields: Field[];
  groupByFieldName?: string;
  overrideDependentFields?: boolean;
}

export const useCreateAggregationBuilderWithMetrics = () => {
  const retailerId = useAppSelector((state) => state.retailer.id);

  return useCallback(
    ({ indexName, fields, groupByFieldName, overrideDependentFields }: UseAggregationBuilderWithMetricsArgs) => {
      const indexFields = fields.map((field) => INDEX_FIELDS.getField(getSearchAppName(), indexName, field.name));
      const builder = new AggregationBuilder(groupByFieldName || 'weekId').addConditionTermFilter('retailerId', [
        retailerId
      ]);
      indexFields.forEach((indexField, index) => {
        if (indexField.dependentFields && !overrideDependentFields) {
          indexField.dependentFields.forEach((dependentField) => {
            builder.addAggregationField(
              dependentField.displayName,
              dependentField.name,
              dependentField.aggregationFunction as string,
              true
            );
          });
        }
        builder.addAggregationField(
          indexField.displayName,
          indexField.name,
          indexField.aggregationFunction as string,
          fields[index].canBeExported || true,
          indexField.aggregationFunctionExpression
        );
      });

      return builder;
    },
    [retailerId]
  );
};

/**
 * Returns an aggregation builder given a list of metrics.
 */
export const useAggregationBuilderWithMetrics = (args: UseAggregationBuilderWithMetricsArgs) => {
  const createAggregationBuilderWithMetrics = useCreateAggregationBuilderWithMetrics();
  return createAggregationBuilderWithMetrics(args);
};
